import type {
  ApiErrorShape,
  AppConfig,
  BootstrapData,
  DailyPayload,
  DailyResponse,
  HistoryItem,
  MonthlyResult,
  OpenReceivable,
  SaveResponse,
  VoidDailyResponse,
} from "../../shared/domain";
import { demoApi } from "./demo";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

const demoMode = import.meta.env.VITE_DEMO_MODE === "true";

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
  });
  const data = (await response.json().catch(() => null)) as T | ApiErrorShape | null;
  if (!response.ok) {
    const error = data as ApiErrorShape | null;
    if (response.status === 401 && url !== "/api/session") {
      window.dispatchEvent(new Event("dnc:unauthorized"));
    }
    throw new ApiError(
      response.status,
      error?.code ?? "NETWORK_ERROR",
      error?.message ?? "通信に失敗しました",
      error?.details,
    );
  }
  return data as T;
}

export const api = demoMode
  ? demoApi
  : {
      getSession: () => request<{ authenticated: boolean }>("/api/session"),
      login: (password: string) => request<{ authenticated: true }>("/api/session", {
        method: "POST",
        body: JSON.stringify({ password }),
      }),
      logout: () => request<{ authenticated: false }>("/api/session", { method: "DELETE" }),
      getBootstrap: (date: string) => request<BootstrapData>(`/api/bootstrap?date=${encodeURIComponent(date)}`),
      getConfig: () => request<AppConfig>("/api/config"),
      getDaily: (date: string) => request<DailyResponse>(`/api/daily?date=${encodeURIComponent(date)}`),
      saveDaily: (payload: DailyPayload) =>
        request<SaveResponse>(`/api/daily/${encodeURIComponent(payload.businessDate)}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        }),
      deleteDaily: (date: string) => request<VoidDailyResponse>(`/api/daily/${encodeURIComponent(date)}`, {
        method: "DELETE",
        body: JSON.stringify({ confirmation: date }),
      }),
      getHistory: (from: string, to: string) =>
        request<HistoryItem[]>(`/api/history?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
      getMonthly: (month: string) =>
        request<MonthlyResult>(`/api/monthly?month=${encodeURIComponent(month)}`),
      getReceivables: () => request<OpenReceivable[]>("/api/receivables?status=open"),
    };

export const isDemoMode = demoMode;
